import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { API_BASE_URL } from "../../Config";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material UI components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Card from "@mui/material/Card";

function ChangePassword() {
  const showSidebar = useSelector((state) => state.sidebar.showSidebar);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [currentPasswordValid, setCurrentPasswordValid] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState("");

  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const [loading, setLoading] = useState(false);
  const [validatingCurrentPassword, setValidatingCurrentPassword] = useState(false);

  const cid = sessionStorage.getItem("cid");
  const Accode = sessionStorage.getItem("accode");

  // ==========================================================
  // CURRENT PASSWORD ON BLUR
  // ==========================================================

  const handleCurrentPasswordBlur = async () => {
    if (!currentPassword) {
      setCurrentPasswordValid(false);
      setCurrentPasswordError("");
      return;
    }

    setValidatingCurrentPassword(true);
    setCurrentPasswordError("");

    try {
      const response = await axios.get(`${API_BASE_URL}/auth/getValidateCurrentPassword`, {
        params: {
          cid: cid,
          username: Accode,
          password: currentPassword,
        },
      });

      if (response.status === 200 && response.data === 1) {
        setCurrentPasswordValid(true);
        setCurrentPasswordError("");
      } else {
        setCurrentPasswordValid(false);
        setCurrentPassword("");
        setCurrentPasswordError("Invalid current password.");
      }
    } catch (error) {
      console.error("Current password validation error:", error);

      setCurrentPasswordValid(false);
      setCurrentPassword("");

      if (error.response?.status === 401) {
        setCurrentPasswordError("Invalid current password.");
      } else {
        setCurrentPasswordError("Unable to validate current password.");
      }
    } finally {
      setValidatingCurrentPassword(false);
    }
  };

  // ==========================================================
  // CURRENT PASSWORD CHANGE
  // ==========================================================

  const handleCurrentPasswordChange = (e) => {
    setCurrentPassword(e.target.value);
    setCurrentPasswordValid(false);
    setCurrentPasswordError("");
  };

  // ==========================================================
  // NEW PASSWORD CHANGE
  // ==========================================================

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;

    setNewPassword(value);

    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError("Does not match password");
    } else {
      setConfirmPasswordError("");
    }
  };

  // ==========================================================
  // CONFIRM PASSWORD ON BLUR
  // ==========================================================

  const handleConfirmPasswordBlur = () => {
    if (!confirmPassword) {
      setConfirmPasswordError("");
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Does not match password");
    } else {
      setConfirmPasswordError("");
    }
  };

  // ==========================================================
  // CONFIRM PASSWORD CHANGE
  // ==========================================================

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;

    setConfirmPassword(value);

    if (value === newPassword) {
      setConfirmPasswordError("");
    }
  };

  // ==========================================================
  // SUBMIT
  // ==========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentPasswordValid) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Current Password",
        text: "Please enter a valid current password.",
      });
      return;
    }

    if (!newPassword) {
      Swal.fire({
        icon: "warning",
        title: "New Password Required",
        text: "Please enter your new password.",
      });
      return;
    }

    if (!confirmPassword) {
      Swal.fire({
        icon: "warning",
        title: "Confirm Password Required",
        text: "Please confirm your new password.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Does not match password");
      return;
    }

    if (currentPassword === newPassword) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Password",
        text: "New password must be different from current password.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/auth/updatePassword`, {
        params: {
          cid: cid,
          username: Accode,
          password: newPassword,
        },
      });

      if (response.status === 200 && response.data === "Password updated successfully.") {
        Swal.fire({
          icon: "success",
          title: "Password Updated!",
          text: "Your password has been updated successfully.",
          timer: 1800,
          showConfirmButton: false,
        });

        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        setCurrentPasswordValid(false);
        setCurrentPasswordError("");
        setConfirmPasswordError("");

        // Hide passwords again
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
      } else {
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: "Password was not updated.",
        });
      }
    } catch (error) {
      console.error("Password update error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data || "Something went wrong while updating password.",
      });
    } finally {
      setLoading(false);
    }
  };

  // SAVE BUTTON CONDITION

  const isSaveDisabled =
    loading ||
    validatingCurrentPassword ||
    !currentPassword ||
    !currentPasswordValid ||
    !newPassword ||
    !confirmPassword ||
    !!confirmPasswordError;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox
        className={`mt-[24%] sm:mt-10 md:mt-0 lg:mt-0 2xl:mt-0 h-[80%] ${
          showSidebar
            ? "transition-all transition-slowest ease ml-20 lg:ml-[18%]"
            : "transition-all transition-slowest ease"
        }`}
      >
        <Card
          sx={{
            p: 3,
            boxShadow: 3,
            marginTop: 3,
          }}
        >
          <MDBox component="form" onSubmit={handleSubmit} borderRadius="lg">
            {/* CURRENT PASSWORD */}

            <MDBox
              mb={3}
              width={{
                xs: "100%",
                sm: "48%",
                lg: "35%",
              }}
            >
              <MDTypography variant="caption" fontWeight="medium" color="text">
                Current Password <span style={{ color: "red" }}>*</span>
              </MDTypography>

              <MDBox position="relative" display="flex" alignItems="center">
                <MDInput
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={handleCurrentPasswordChange}
                  onBlur={handleCurrentPasswordBlur}
                  placeholder="Enter current password"
                  fullWidth
                  required
                />

                <FontAwesomeIcon
                  icon={showCurrentPassword ? faEyeSlash : faEye}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    cursor: "pointer",
                    zIndex: 2,
                  }}
                />
              </MDBox>

              {validatingCurrentPassword && (
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  Validating password...
                </MDTypography>
              )}

              {currentPasswordError && (
                <MDTypography
                  variant="caption"
                  color="error"
                  sx={{
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  {currentPasswordError}
                </MDTypography>
              )}

              {currentPasswordValid && (
                <MDTypography
                  variant="caption"
                  color="success"
                  sx={{
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  Current password verified.
                </MDTypography>
              )}
            </MDBox>

            {/* NEW PASSWORD */}

            <MDBox
              mb={3}
              width={{
                xs: "100%",
                sm: "48%",
                lg: "35%",
              }}
            >
              <MDTypography variant="caption" fontWeight="medium" color="text">
                New Password <span style={{ color: "red" }}>*</span>
              </MDTypography>

              <MDBox position="relative" display="flex" alignItems="center">
                <MDInput
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  placeholder="Enter new password"
                  fullWidth
                  required
                />

                <FontAwesomeIcon
                  icon={showNewPassword ? faEyeSlash : faEye}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    cursor: "pointer",
                    zIndex: 2,
                  }}
                />
              </MDBox>
            </MDBox>

            {/* CONFIRM PASSWORD */}

            <MDBox
              mb={3}
              width={{
                xs: "100%",
                sm: "48%",
                lg: "35%",
              }}
            >
              <MDTypography variant="caption" fontWeight="medium" color="text">
                Confirm Password <span style={{ color: "red" }}>*</span>
              </MDTypography>

              <MDBox position="relative" display="flex" alignItems="center">
                <MDInput
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  onBlur={handleConfirmPasswordBlur}
                  placeholder="Confirm new password"
                  fullWidth
                  required
                />

                <FontAwesomeIcon
                  icon={showConfirmPassword ? faEyeSlash : faEye}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    cursor: "pointer",
                    zIndex: 2,
                  }}
                />
              </MDBox>

              {confirmPasswordError && (
                <MDTypography
                  variant="caption"
                  color="error"
                  sx={{
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  {confirmPasswordError}
                </MDTypography>
              )}
            </MDBox>

            {/* ================================================= */}
            {/* UPDATE BUTTON */}
            {/* ================================================= */}

            <MDBox display="flex" mt={3}>
              <MDButton type="submit" variant="gradient" color="info" disabled={isSaveDisabled}>
                {loading ? "Updating..." : "Update Password"}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default ChangePassword;
